import { API_V1_BASE } from '@/config/apiBase';
import { mediaUrlFromApi } from '@/utils/mediaUrlFromApi';

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    next: { revalidate: 60 }, // Cache for 60 seconds to improve TTFB
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }
  return res.json();
}

function apiUrl(path) {
  const base = API_V1_BASE.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export async function getCountryPage(countrySlug) {
  const slug = String(countrySlug || '').trim();
  if (!slug) return null;
  return fetchJson(apiUrl(`/destinations/countries/${encodeURIComponent(slug)}`));
}

export async function getCityPage(countrySlug, citySlug) {
  const c = String(countrySlug || '').trim();
  const city = String(citySlug || '').trim();
  if (!c || !city) return null;
  const data = await fetchJson(
    apiUrl(
      `/destinations/countries/${encodeURIComponent(c)}/cities/${encodeURIComponent(city)}`,
    ),
  );
  return data?.city || null;
}

/** Published countries with cities from CMS. Returns [] when CMS empty — never invent. */
export async function listPublishedDestinations() {
  const data = await fetchJson(apiUrl('/destinations/countries'));
  if (!data) return [];
  return Array.isArray(data.countries) ? data.countries : [];
}

/**
 * Flatten published CMS cities into Where-to-Go chip items.
 * Empty CMS → []. Never invent sample destinations (TA-064).
 */
export function destinationsToWhereToGoItems(countries) {
  const items = [];
  let n = 0;
  for (const c of countries || []) {
    const countrySlug = c.slug || '';
    for (const city of c.cities || []) {
      if (!city?.name) continue;
      n += 1;
      items.push({
        id: city.id || `${countrySlug}-${city.slug || n}`,
        number: String(n).padStart(2, '0'),
        title: city.name,
        keyword: city.slug || city.name,
        href:
          countrySlug && city.slug
            ? `/destinations/${encodeURIComponent(countrySlug)}/${encodeURIComponent(city.slug)}`
            : undefined,
      });
    }
  }
  return items;
}

/** Normalize CMS whereToGoNext chips (from country/city detail). */
export function normalizeWhereToGoNext(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list
    .filter((item) => item && (item.title || item.name))
    .map((item, index) => ({
      id: item.id || `wtg-${index}`,
      number: item.number || String(index + 1).padStart(2, '0'),
      title: item.title || item.name || '',
      keyword: item.keyword || item.slug || '',
      href: item.href,
      ...item,
    }));
}

/**
 * Blog hero carousel slides from published CMS cities.
 * Empty CMS → []. Never invent static Japan product-spec slides (TA-065).
 */
export function destinationsToBlogHeroSlides(countries) {
  const slides = [];
  for (const c of countries || []) {
    const countrySlug = c.slug || '';
    const countryAbout = typeof c.about === 'string' ? c.about.trim() : '';
    for (const city of c.cities || []) {
      if (!city?.name) continue;
      const alt = city.heroImageAlt || city.name;
      slides.push({
        id: city.id || `${countrySlug}-${city.slug}`,
        name: city.name,
        title: city.name,
        image: city.heroImage || '/images/place-1.jpg',
        imageAlt: alt,
        imageTitle: alt,
        description: countryAbout,
        href:
          countrySlug && city.slug
            ? `/destinations/${encodeURIComponent(countrySlug)}/${encodeURIComponent(city.slug)}`
            : undefined,
      });
    }
  }
  return slides;
}

/**
 * Customer mobile/desktop Global Spots nav from published CMS destinations (TA-066).
 * Empty CMS → empty sections (never invent city lists).
 * Countries without published cities still appear (expand shows empty state).
 */
export function destinationsToMobileNav(countries) {
  const attractionCountries = [];
  const destinationSections = [];
  const countryCities = {};
  const countryKeyToServiceNames = {};

  for (const c of countries || []) {
    const slug = String(c.slug || '').trim();
    const name = String(c.name || '').trim();
    if (!slug || !name) continue;

    const cities = (c.cities || [])
      .filter((city) => city?.name && city?.slug)
      .map((city) => {
        const rawImg = city.heroImage || city.hero_image_url || '';
        return {
          id: city.id || `${slug}-${city.slug}`,
          name: city.name,
          image: rawImg
            ? mediaUrlFromApi(String(rawImg).trim())
            : '/images/place-1.jpg',
          url: `/destinations/${encodeURIComponent(slug)}/${encodeURIComponent(city.slug)}`,
        };
      });

    countryKeyToServiceNames[slug] = [name];
    
    attractionCountries.push({
      key: slug,
      label: name.toUpperCase(),
      href: `/destinations/${encodeURIComponent(slug)}`,
      heroImage: c.heroImage
        ? mediaUrlFromApi(String(c.heroImage).trim())
        : '',
    });
    destinationSections.push({ key: slug, label: name, href: `/destinations/${encodeURIComponent(slug)}` });
    countryCities[slug] = cities;
  }

  return {
    attractionCountries,
    destinationSections,
    countryCities,
    countryKeyToServiceNames,
  };
}

/**
 * Homepage hero / "Where to move" country chips from published CMS.
 * Empty CMS → [] (never invent static UK/Japan list).
 */
export function destinationsToHeroChips(countries) {
  return (countries || [])
    .filter((c) => c?.name && c?.slug)
    .map((c) => {
      const rawImg = c.heroImage || c.hero_image_url || '';
      return {
        id: c.id || c.slug,
        name: c.name,
        image: rawImg
          ? mediaUrlFromApi(String(rawImg).trim())
          : '/images/place-1.jpg',
        link: `/destinations/${encodeURIComponent(c.slug)}`,
      };
    });
}
